export default {
    props: {
        config: { type: Object, required: true },
        initial: { type: Object, required: true },
        fetchProducts: { type: Function, required: true }
    },
    data() {
        return {
            filters: this.initial.filters || [],
            items: this.initial.items || [],
            totalItems: this.initial.totalItems || 0,
            selections: {},   // propertyId -> { min, max } | { values: [] }
            openFilter: null, // propertyId of the open dropdown
            filtersOpen: false, // mobile filter aside visibility
            dirty: false,     // a filter changed while the aside was open (apply-on-close)
            skip: 0,
            loading: false,
            debounceTimer: null
        };
    },
    computed: {
        hasMore() { return this.items.length < this.totalItems; }
    },
    mounted() {
        document.addEventListener('keydown', this.onKeydown);
    },
    unmounted() {
        document.removeEventListener('keydown', this.onKeydown);
        document.body.classList.remove('noscroll');
    },
    methods: {
        isRange(f) { return f.type === 'numeric' || f.type === 'decimal'; },
        toggleFilter(id) { this.openFilter = this.openFilter === id ? null : id; },
        openFilters() {
            this.filtersOpen = true;
            document.body.classList.add('noscroll');
            this.$nextTick(() => { if (this.$refs.filtersClose) this.$refs.filtersClose.focus(); });
        },
        closeFilters() {
            if (!this.filtersOpen) return;
            this.filtersOpen = false;
            document.body.classList.remove('noscroll');
            if (this.dirty) { this.dirty = false; this.reload(); }
        },
        onKeydown(e) {
            if (e.key === 'Escape' && this.filtersOpen) this.closeFilters();
        },
        selectionFor(f) {
            if (!this.selections[f.propertyId]) {
                this.selections[f.propertyId] = this.isRange(f)
                    ? { min: f.min, max: f.max }
                    : { values: [] };
            }
            return this.selections[f.propertyId];
        },
        toggleValue(f, value) {
            const sel = this.selectionFor(f);
            const i = sel.values.indexOf(value);
            if (i === -1) sel.values.push(value); else sel.values.splice(i, 1);
            this.queueReload();
        },
        isChecked(f, value) {
            const sel = this.selections[f.propertyId];
            return !!(sel && sel.values && sel.values.indexOf(value) !== -1);
        },
        resetFilter(f) {
            if (this.isRange(f)) this.selections[f.propertyId] = { min: f.min, max: f.max };
            else this.selections[f.propertyId] = { values: [] };
            this.queueReload();
        },
        buildFilterPayload() {
            return this.filters.map(f => {
                const sel = this.selections[f.propertyId];
                if (!sel) return null;
                if (this.isRange(f)) return { propertyId: f.propertyId, min: sel.min, max: sel.max };
                return { propertyId: f.propertyId, values: sel.values };
            }).filter(Boolean);
        },
        queueReload() {
            // In the mobile aside, defer filtering until the user closes it (apply-on-close).
            // On desktop the aside is never open, so filtering stays live.
            if (this.filtersOpen) { this.dirty = true; return; }
            clearTimeout(this.debounceTimer);
            this.debounceTimer = setTimeout(() => this.reload(), 250);
        },
        reload() {
            this.loading = true;
            this.skip = 0;
            this.fetchProducts({
                categoryNodeId: this.config.categoryNodeId,
                skip: 0, take: this.config.pageSize,
                cultureCode: this.config.culture,
                filterProperties: this.config.filterProperties,
                filters: this.buildFilterPayload()
            }).then(data => {
                this.items = data.items || [];
                this.totalItems = data.totalItems || 0;
            }).finally(() => { this.loading = false; });
        },
        loadMore() {
            this.loading = true;
            this.skip += this.config.pageSize;
            this.fetchProducts({
                categoryNodeId: this.config.categoryNodeId,
                skip: this.skip, take: this.config.pageSize,
                cultureCode: this.config.culture,
                filterProperties: this.config.filterProperties,
                filters: this.buildFilterPayload()
            }).then(data => {
                this.items = this.items.concat(data.items || []);
                this.totalItems = data.totalItems || 0;
            }).finally(() => { this.loading = false; });
        }
    },
    template: `
<div class="category-overview__inner">
    <button v-if="filters.length" type="button" class="category-overview__filters-trigger" @click="openFilters">
        {{ config.filterLabel }}
    </button>

    <div v-if="filters.length" class="category-overview__filters-backdrop"
         :class="{ 'is-open': filtersOpen }" @click="closeFilters"></div>

    <div v-if="filters.length" class="category-overview__filters" :class="{ 'is-open': filtersOpen }"
         role="dialog" aria-modal="true" :aria-label="config.filterLabel">
        <div class="category-overview__filters-head">
            <span class="category-overview__filters-title">{{ config.filterLabel }}</span>
            <button type="button" ref="filtersClose" class="category-overview__filters-close"
                    @click="closeFilters" :aria-label="config.filterLabel"></button>
        </div>
        <span class="category-overview__filters-label">{{ config.filterLabel }}</span>
        <div v-for="f in filters" :key="f.propertyId" class="category-overview__filter"
             :class="{ 'is-open': openFilter === f.propertyId }">
            <button type="button" class="category-overview__filter-toggle" @click="toggleFilter(f.propertyId)">
                <span>{{ f.filterName || f.displayName }}</span>
                <span class="category-overview__filter-chevron" aria-hidden="true"></span>
            </button>
            <div v-show="openFilter === f.propertyId" class="category-overview__filter-panel">
                <template v-if="isRange(f)">
                    <div class="category-overview__range">
                        <label>Min <input type="number" :step="f.type === 'decimal' ? 'any' : 1"
                            v-model.number="selectionFor(f).min" @input="queueReload" :min="f.min" :max="f.max"></label>
                        <label>Max <input type="number" :step="f.type === 'decimal' ? 'any' : 1"
                            v-model.number="selectionFor(f).max" @input="queueReload" :min="f.min" :max="f.max"></label>
                    </div>
                    <span v-if="f.unit" class="category-overview__range-unit">{{ f.unit }}</span>
                </template>
                <template v-else>
                    <label v-for="v in (f.values || [])" :key="v" class="category-overview__option">
                        <input type="checkbox" :checked="isChecked(f, v)" @change="toggleValue(f, v)"> {{ v }}
                    </label>
                </template>
                <button type="button" class="category-overview__filter-reset" @click="resetFilter(f)">Nulstil</button>
            </div>
        </div>
    </div>

    <ul v-if="items.length" class="category-overview__grid">
        <li v-for="item in items" :key="item.id" class="category-overview__card product-card">
            <a :href="item.url" class="product-card__link">
                <div class="product-card__image">
                    <img v-if="item.imageUrl" :src="item.imageUrl" :alt="item.modelName" loading="lazy">
                </div>
                <div class="product-card__body">
                    <p v-if="item.brandName" class="product-card__brand">{{ item.brandName }}</p>
                    <h3 class="product-card__model">{{ item.modelName }}</h3>
                    <dl v-if="item.stats.length" class="product-card__stats">
                        <div v-for="(s, i) in item.stats" :key="i" class="product-card__stat">
                            <dt>{{ s.label }}</dt>
                            <dd>{{ s.value }}<template v-if="s.unit"> {{ s.unit }}</template></dd>
                        </div>
                    </dl>
                    <span class="product-card__cta cta-button secondary">
                        <span class="product-card__cta-text">Mere information</span>
                        <span class="cta-button__icon" aria-hidden="true"></span>
                    </span>
                </div>
            </a>
        </li>
    </ul>
    <p v-else class="category-overview__empty">Ingen produkter fundet.</p>

    <div v-if="hasMore" class="category-overview__more">
        <button type="button" class="cta-button primary" :disabled="loading" @click="loadMore">
            <span>Indlæs flere</span><span class="cta-button__icon" aria-hidden="true"></span>
        </button>
    </div>
</div>
`
};
